import {
  normalizeCode,
  type OrgExamType,
} from "@petergok/blue-collar-shared";
import { isOrgExamType } from "@/utils/orgPurchases/config";

const PENDING_CODE_KEY = "pending_access_code";
const PENDING_META_KEY = "pending_access_code_meta";
const PENDING_ACCESS_CODE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface PendingAccessCode {
  code: string;
  examType: OrgExamType;
  firstSeenAt: number;
}

interface PendingAccessCodeMeta {
  code?: string;
  examType: OrgExamType;
  firstSeenAt: number;
}

function restoreStorageValue(key: string, value: string | null) {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch {
    // A mismatched pair is more dangerous than dropping an uncommitted record.
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage is completely unavailable.
    }
  }
}

export function capturePendingAccessCode(
  rawCode: string | null,
  rawExam: string | null,
): boolean {
  const code = normalizeCode(rawCode ?? "");
  if (!code || !isOrgExamType(rawExam)) return false;

  let previousCode: string | null = null;
  let previousMeta: string | null = null;

  try {
    previousCode = localStorage.getItem(PENDING_CODE_KEY);
    previousMeta = localStorage.getItem(PENDING_META_KEY);
    const existing = readPendingAccessCode();
    const existingMeta = readPendingAccessCodeMeta();
    const isSameLink =
      existing?.code === code && existingMeta?.examType === rawExam;

    localStorage.setItem(PENDING_CODE_KEY, code);
    localStorage.setItem(
      PENDING_META_KEY,
      JSON.stringify({
        code,
        examType: rawExam,
        // The seven-day TTL is anchored to the first link tap, not refreshed by
        // repeatedly visiting the same URL. A different link starts a fresh
        // window so an old abandoned code cannot immediately expire its replacement.
        firstSeenAt: isSameLink ? existingMeta.firstSeenAt : Date.now(),
      }),
    );
    return true;
  } catch {
    restoreStorageValue(PENDING_CODE_KEY, previousCode);
    restoreStorageValue(PENDING_META_KEY, previousMeta);
    // Storage can be unavailable in private browser modes. Redemption remains
    // possible manually from the profile modal.
    return false;
  }
}

function readPendingAccessCodeMeta(): PendingAccessCodeMeta | null {
  try {
    const raw = localStorage.getItem(PENDING_META_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      (parsed?.code !== undefined &&
        (typeof parsed.code !== "string" || !normalizeCode(parsed.code))) ||
      typeof parsed?.firstSeenAt !== "number" ||
      !Number.isFinite(parsed.firstSeenAt) ||
      !isOrgExamType(parsed?.examType)
    ) {
      return null;
    }
    return {
      ...parsed,
      code: parsed.code ? normalizeCode(parsed.code) : undefined,
    };
  } catch {
    return null;
  }
}

/** Reads the pending record and lazily removes it when its seven-day TTL expires. */
export function readPendingAccessCode(): PendingAccessCode | null {
  try {
    const code = normalizeCode(localStorage.getItem(PENDING_CODE_KEY) ?? "");
    const meta = readPendingAccessCodeMeta();
    if (!code || !meta || (meta.code && meta.code !== code)) return null;

    if (Date.now() - meta.firstSeenAt > PENDING_ACCESS_CODE_TTL_MS) {
      clearPendingAccessCode();
      return null;
    }

    return { code, examType: meta.examType, firstSeenAt: meta.firstSeenAt };
  } catch {
    return null;
  }
}

export function clearPendingAccessCode(expected?: PendingAccessCode) {
  try {
    if (expected) {
      const current = readPendingAccessCode();
      if (
        !current ||
        getPendingAccessCodeIdentity(current) !==
          getPendingAccessCodeIdentity(expected)
      ) {
        return;
      }
    }

    localStorage.removeItem(PENDING_CODE_KEY);
    localStorage.removeItem(PENDING_META_KEY);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}

export function getPendingAccessCodeIdentity(pending: PendingAccessCode) {
  return `${pending.code}:${pending.examType}:${pending.firstSeenAt}`;
}
